import { mediaUrl, useSiteMedia } from "../lib/siteMedia";
import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import SectionHeader from "../components/SectionHeader";

const MENU = {
  Starters: [
    { name: "Yellowfin Tuna Crudo", desc: "Bronze fennel, blood orange, espelette, citrus oil", price: 28 },
    { name: "Wagyu Tartare", desc: "Smoked yolk, capers, brioche, black pepper", price: 32 },
    { name: "Maine Lobster Bisque", desc: "Cognac, crème fraîche, chervil", price: 26 },
    { name: "Burrata", desc: "Charred peach, prosciutto di Parma, basil, twenty-year balsamic", price: 24 },
  ],
  Mains: [
    { name: "44oz Tomahawk", desc: "Dry-aged 35 days. Bone marrow butter. (Serves two)", price: 195 },
    { name: "Filet Mignon", desc: "Center cut. Truffle pommes purée. Bordelaise.", price: 78 },
    { name: "Wild Halibut", desc: "Brown butter, lemon caper, asparagus", price: 62 },
    { name: "Lobster Linguine", desc: "Cold-water tail, saffron, tomato confit", price: 68 },
    { name: "Roasted Veal Chop", desc: "Wild mushrooms, demi-glace, gratin", price: 84 },
  ],
  "Raw Bar": [
    { name: "Oysters — East / West", desc: "Half-dozen. Mignonette, cocktail.", price: 32 },
    { name: "Tower for Two", desc: "Oysters, shrimp, crab, lobster, tuna tartare", price: 145 },
    { name: "Caviar — Imperial Osetra", desc: "Per 30g. Crème fraîche, blini.", price: 220 },
  ],
  Desserts: [
    { name: "Tableside Crème Brûlée", desc: "Madagascar vanilla, demerara, cognac", price: 18 },
    { name: "Chocolate Soufflé", desc: "70% Valrhona, vanilla bean ice cream", price: 22 },
    { name: "Cheese Course", desc: "Three cheeses, fig, honeycomb, marcona", price: 28 },
  ],
};

export default function Restaurant() {
  const media = useSiteMedia();
  return (
    <div data-testid="restaurant-page">
      <section className="relative pt-32 min-h-[80vh] flex items-end px-6 sm:px-10 lg:px-16 overflow-hidden">
        <div className="absolute inset-0">
          <img src={mediaUrl(media, "restaurant_hero")} alt="" className="w-full h-full object-cover ken-burns" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative max-w-[1600px] mx-auto pb-20 w-full">
          <p className="eyebrow">The Kitchen</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-8xl mt-6 leading-[1.02] tracking-tight max-w-4xl">
            Wood-fired, <span className="italic" style={{ color: "var(--twr-bronze)" }}>quietly perfected.</span>
          </h1>
          <p className="text-[var(--twr-warm-white)]/80 text-lg mt-6 max-w-2xl">
            Florida coast and farm. Dry-aged prime cuts. Caviar service. A wine list curated for those who don't read wine lists.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/reservations?type=restaurant" data-testid="restaurant-reserve" className="btn-bronze">Reserve a Table <ChevronRight size={14} /></Link>
            <a href="#menu" className="btn-ghost">View the Menu</a>
          </div>
        </div>
      </section>

      <section className="section" id="menu">
        <div className="max-w-[1400px] mx-auto">
          <SectionHeader eyebrow="The Menu" title="Chef's selection — winter '26" kicker="Subject to the seasons, the boats, and what the kitchen finds beautiful that morning." />
          <div className="grid lg:grid-cols-2 gap-x-16 gap-y-12 mt-16">
            {Object.entries(MENU).map(([section, items]) => (
              <div key={section} data-testid={`menu-section-${section.toLowerCase().replace(/\s+/g, '-')}`}>
                <h3 className="font-serif text-3xl mb-8" style={{ color: "var(--twr-bronze)" }}>{section}</h3>
                <div className="space-y-7">
                  {items.map((d) => (
                    <div key={d.name} className="flex items-baseline gap-4">
                      <div className="flex-1">
                        <p className="font-serif text-lg">{d.name}</p>
                        <p className="text-[var(--twr-warm-white)]/60 text-sm mt-1 leading-relaxed">{d.desc}</p>
                      </div>
                      <span className="font-serif text-lg text-[var(--twr-bronze)] whitespace-nowrap">${d.price}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section bg-[#0c0b0a]">
        <div className="max-w-[1600px] mx-auto grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <div className="lg:col-span-6">
            <p className="eyebrow">Hours</p>
            <h2 className="font-serif text-4xl sm:text-5xl mt-4 leading-tight">When we're open.</h2>
            <div className="mt-10 space-y-4">
              {[
                ["Monday — Thursday", "Lunch 11:30 — 14:30 · Dinner 17:30 — 23:00"],
                ["Friday", "Lunch 11:30 — 14:30 · Dinner 17:30 — 00:00"],
                ["Saturday", "Brunch 10:00 — 15:00 · Dinner 17:00 — 00:00"],
                ["Sunday", "Brunch 10:00 — 15:00 · Dinner 17:00 — 22:00"],
              ].map(([d, t]) => (
                <div key={d} className="flex items-baseline justify-between py-4 border-b border-[var(--twr-line)] gap-4">
                  <span className="font-serif text-lg">{d}</span>
                  <span className="text-[var(--twr-warm-white)]/65 text-sm text-right">{t}</span>
                </div>
              ))}
            </div>
            <Link to="/reservations?type=restaurant" className="btn-bronze mt-10">Reserve a Table <ChevronRight size={14} /></Link>
          </div>
          <div className="lg:col-span-6 frame aspect-[5/6]">
            <img src={mediaUrl(media, "restaurant_bottom")} alt="Dining room" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>
    </div>
  );
}
